import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Alert, Tag, Button, Space } from 'antd';
import { ReloadOutlined, CodeOutlined } from '@ant-design/icons';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import { api } from '../api';
import { t } from '../i18n';

type Status = 'connecting' | 'open' | 'closed';

/** Superadmin web console: a real PTY on the server host bridged over WebSocket
 * into an xterm.js terminal. */
export function Console() {
  const avail = useQuery({ queryKey: ['console-available'], queryFn: api.consoleAvailable });
  const host = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<Status>('connecting');
  const [nonce, setNonce] = useState(0); // bump to reconnect

  useEffect(() => {
    if (!host.current || avail.data?.available === false) return;
    const term = new Terminal({
      cursorBlink: true,
      fontFamily: 'Menlo, Consolas, "DejaVu Sans Mono", monospace',
      fontSize: 13,
      theme: { background: '#0d1220', foreground: '#e8ecf4', cursor: '#5b8def' },
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(host.current);
    fit.fit();

    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${proto}//${location.host}/api/admin/v1/console/ws`);
    setStatus('connecting');

    const sendResize = (): void => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ t: 'r', c: term.cols, r: term.rows }));
      }
    };

    ws.onopen = () => {
      setStatus('open');
      term.focus();
      sendResize();
    };
    ws.onmessage = (ev) => term.write(typeof ev.data === 'string' ? ev.data : '');
    ws.onclose = () => setStatus('closed');
    ws.onerror = () => setStatus('closed');

    const dataSub = term.onData((d) => {
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ t: 'i', d }));
    });

    const onResize = (): void => {
      try {
        fit.fit();
        sendResize();
      } catch {
        /* terminal detached */
      }
    };
    window.addEventListener('resize', onResize);
    // Initial fit after layout settles.
    const raf = requestAnimationFrame(onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      dataSub.dispose();
      try {
        ws.close();
      } catch {
        /* already closed */
      }
      term.dispose();
    };
  }, [avail.data?.available, nonce]);

  if (avail.data && !avail.data.available) {
    return (
      <Card title={<Space><CodeOutlined />{t('consoleTitle')}</Space>}>
        <Alert type="warning" showIcon message={t('consoleUnavailable')} description={t('consoleUnavailableHint')} />
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <CodeOutlined />
          {t('consoleTitle')}
          <Tag color={status === 'open' ? 'green' : status === 'connecting' ? 'blue' : 'red'}>
            {status === 'open' ? t('consoleOnline') : status === 'connecting' ? t('consoleConnecting') : t('consoleClosed')}
          </Tag>
        </Space>
      }
      extra={
        <Button size="small" icon={<ReloadOutlined />} onClick={() => setNonce((n) => n + 1)}>
          {t('consoleReconnect')}
        </Button>
      }
      styles={{ body: { padding: 8 } }}
    >
      <div
        ref={host}
        style={{ height: 'calc(100vh - 200px)', minHeight: 360, background: '#0d1220', borderRadius: 6, padding: 6 }}
      />
    </Card>
  );
}
